import { useEffect, useState } from 'react';
import type { MutualFund } from '../types/MutualFund';
import { getFunds } from '../api/funds';
import { setTenantId } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

// Module-level cache. /api/funds is tenant-scoped, so the cache must also be
// tenant-scoped — we record which tenant the data belongs to and drop it
// when a consumer mounts under a different tenant. Defensive future-proofing:
// today the tenant doesn't change mid-session, but a future tenant switcher
// would rely on this invariant.
let cache: MutualFund[] | null = null;
let cachedForTenantId: number | null = null;
let inflight: Promise<MutualFund[]> | null = null;

function loadFunds(tenantId: number): Promise<MutualFund[]> {
  if (cache && cachedForTenantId === tenantId) return Promise.resolve(cache);
  if (inflight) return inflight;
  // Defensive: write the tenant id into api/client.ts module state before
  // the request fires. AuthContext also writes this from its own [user]
  // effect, but on a hard refresh of a tenant-scoped page React's
  // child-first effect ordering runs useFunds's effect BEFORE
  // AuthContext's setTenantId effect — apiGet would otherwise go out
  // without X-Tenant-Id and the backend returns 400 tenant_required.
  // Calling setTenantId here makes the hook self-sufficient regardless of
  // effect ordering.
  setTenantId(tenantId);
  inflight = getFunds()
    .then((data) => {
      cache = data;
      cachedForTenantId = tenantId;
      inflight = null;
      return data;
    })
    .catch((err: unknown) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

interface UseFundsResult {
  data: MutualFund[] | null;
  loading: boolean;
  error: string | null;
}

export function useFunds(): UseFundsResult {
  const { user } = useAuth();
  const tenantId = user?.currentTenant.tenantId ?? null;

  const [state, setState] = useState<UseFundsResult>(() => {
    if (cache && cachedForTenantId === tenantId) {
      return { data: cache, loading: false, error: null };
    }
    return { data: null, loading: true, error: null };
  });

  useEffect(() => {
    // Wait until AuthContext has resolved a tenant. Without one the request
    // would 400 with tenant_required — that's the bug this hook is fixing.
    if (tenantId === null) return;

    // Cache belongs to a different tenant — drop it before fetching fresh.
    if (cache && cachedForTenantId !== tenantId) {
      cache = null;
      cachedForTenantId = null;
    }

    if (cache && cachedForTenantId === tenantId) {
      setState((prev) =>
        prev.data === cache
          ? prev
          : { data: cache, loading: false, error: null },
      );
      return;
    }

    let cancelled = false;
    loadFunds(tenantId)
      .then((data) => {
        if (cancelled) return;
        setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ data: null, loading: false, error: message });
      });

    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  return state;
}
